import { useEffect, useRef } from 'react';

interface ErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  message: string;
  code?: string;
  suggestion?: string;
  actionLabel?: string;
  onAction?: () => void;
}

export function ErrorModal({
  isOpen,
  onClose,
  title = 'Erro ao processar',
  message,
  code,
  suggestion,
  actionLabel,
  onAction
}: ErrorModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  // Fechar com ESC
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  // Fechar ao clicar fora
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={handleBackdropClick}
    >
      <div
        ref={modalRef}
        className="w-full max-w-md bg-[var(--cg-surface)] rounded-2xl shadow-2xl border border-[var(--cg-border)] animate-in zoom-in-95 duration-200"
      >
        {/* Header */}
        <div className="p-6 pb-4">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
              <i className="fa-solid fa-triangle-exclamation text-red-400 text-xl"></i>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-bold text-red-400 mb-1">{title}</h3>
              <p className="text-sm opacity-90 leading-relaxed">{message}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors p-1"
            >
              <i className="fa-solid fa-times"></i>
            </button>
          </div>
        </div>

        {/* Suggestion */}
        {suggestion && (
          <div className="px-6 pb-4">
            <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <div className="flex items-start gap-2">
                <i className="fa-solid fa-lightbulb text-blue-400 text-sm mt-0.5"></i>
                <p className="text-xs text-blue-300">{suggestion}</p>
              </div>
            </div>
          </div>
        )}

        {/* Code (for debugging) */}
        {code && (
          <div className="px-6 pb-4">
            <div className="text-[10px] opacity-40 font-mono">
              Código: {code}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="p-4 border-t border-[var(--cg-border)] flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="btn h-10 px-4 text-sm"
          >
            Fechar
          </button>
          {actionLabel && onAction && (
            <button
              onClick={() => {
                onAction();
                onClose();
              }}
              className="btn btn-primary h-10 px-4 text-sm"
            >
              {actionLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
